import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Clock, Calendar, Users, Target, CheckCircle2, Circle,
  Link2, Lock, Repeat, MessageSquare, ExternalLink, Loader2,
  Pencil, X, ChevronRight
} from 'lucide-react';
import { format, parseISO, isPast, isToday } from 'date-fns';
import { toast } from 'sonner';

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-green-100 text-green-800 border-green-200',
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
];

const PRIORITY_OPTIONS = ['urgent', 'high', 'medium', 'low'];

export default function TaskDetailDrawer({ task, open, onClose, onUpdate }) {
  const { token } = useAuth();
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && task) {
      fetchComments();
      setEditing(null);
    }
  }, [open, task?.id]);

  const fetchComments = async () => {
    if (!task) return;
    setLoadingComments(true);
    try {
      const res = await api.get(token, `/task-comments/${task.source}/${task.id}`);
      setComments(res.data.comments || []);
    } catch {
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.updateTaskStatus(token, {
        taskId: task.id,
        source: task.source,
        newStatus,
        clientId: task.clientId,
      });
      toast.success(`Status updated to ${newStatus}`);
      onUpdate?.();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await api.post(token, `/task-comments/${task.source}/${task.id}`, {
        content: newComment.trim(),
      });
      setNewComment('');
      fetchComments();
    } catch {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubtaskToggle = async (subtaskIndex) => {
    try {
      const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
      if (task.source === 'lead') {
        await fetch(`${API_BASE}/leads/tasks/${task.id}/checklist/${subtaskIndex}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await fetch(`${API_BASE}/clients/${task.clientId}/workflow/${task.id}/subtasks/${subtaskIndex}/toggle`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      onUpdate?.();
    } catch {
      toast.error('Failed to toggle subtask');
    }
  };

  if (!task) return null;

  const dueDate = task.dueDate ? parseISO(task.dueDate) : null;
  const isOverdue = dueDate && isPast(dueDate) && task.normalizedStatus !== 'completed';
  const subtasks = task.subtasks || task.checklist || [];
  const completedSubs = subtasks.filter(s => s.completed).length;
  const deps = task.dependencies || [];
  const isClient = task.source === 'client';
  const contextLink = task.contextLink || (isClient ? `/app/clients/${task.clientId}/workflow` : `/app/leads`);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto p-0" data-testid="task-detail-drawer">
        <div className="sticky top-0 bg-white z-10 border-b border-[#E6E0D9]">
          <SheetHeader className="p-5 pb-4">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-base font-semibold text-[#2C2420] leading-tight" data-testid="task-detail-title">
                  {task.title}
                </SheetTitle>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Badge className={cn('text-[10px]', isClient ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700')}>
                    {isClient ? <Users className="w-3 h-3 mr-1" /> : <Target className="w-3 h-3 mr-1" />}
                    {task.contextName}
                  </Badge>
                  <Badge className={cn('text-[10px]', PRIORITY_COLORS[task.normalizedPriority] || PRIORITY_COLORS.medium)}>
                    {task.normalizedPriority}
                  </Badge>
                  {(task.recurring || task.recurrencePattern) && (
                    <Badge className="text-[10px] bg-purple-50 text-purple-700">
                      <Repeat className="w-3 h-3 mr-1" />{task.recurrencePattern || 'Recurring'}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </SheetHeader>
        </div>

        <div className="p-5 space-y-6">
          {/* Status & Priority Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium text-[#8C8078] uppercase tracking-wider">Status</Label>
              <Select value={task.normalizedStatus} onValueChange={handleStatusChange}>
                <SelectTrigger className="mt-1 h-9" data-testid="task-detail-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      <span className="flex items-center gap-2">
                        <span className={cn("w-2 h-2 rounded-full", s.color.split(' ')[0])} />
                        {s.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-[#8C8078] uppercase tracking-wider">Priority</Label>
              <Select value={task.normalizedPriority} onValueChange={async (val) => {
                try {
                  await api.batchUpdateTasks(token, {
                    taskIds: [task.id], taskSources: [task.source], action: 'priority', value: val
                  });
                  toast.success('Priority updated');
                  onUpdate?.();
                } catch { toast.error('Failed to update priority'); }
              }}>
                <SelectTrigger className="mt-1 h-9" data-testid="task-detail-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map(p => (
                    <SelectItem key={p} value={p}>
                      <span className="flex items-center gap-2 capitalize">
                        <span className={cn("w-2 h-2 rounded-full", PRIORITY_COLORS[p]?.split(' ')[0])} />
                        {p}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Due Date */}
          <div>
            <Label className="text-xs font-medium text-[#8C8078] uppercase tracking-wider">Due Date</Label>
            <div className={cn(
              "mt-1 flex items-center gap-2 text-sm rounded-lg border px-3 py-2",
              isOverdue ? "border-red-200 bg-red-50 text-red-700" : "border-[#E6E0D9] text-[#2C2420]"
            )} data-testid="task-detail-due-date">
              <Calendar className="w-4 h-4" />
              {dueDate ? format(dueDate, 'EEEE, MMMM d, yyyy') : 'No due date'}
              {isOverdue && <span className="ml-auto text-xs font-medium text-red-600">OVERDUE</span>}
              {dueDate && isToday(dueDate) && <span className="ml-auto text-xs font-medium text-amber-600">TODAY</span>}
            </div>
          </div>

          {/* Owner */}
          <div>
            <Label className="text-xs font-medium text-[#8C8078] uppercase tracking-wider">Assigned To</Label>
            <div className="mt-1 flex items-center gap-2 text-sm border border-[#E6E0D9] rounded-lg px-3 py-2" data-testid="task-detail-owner">
              <Users className="w-4 h-4 text-[#8C8078]" />
              {task.assignedTo || task.owner || 'Unassigned'}
            </div>
          </div>

          {/* Description */}
          {(task.notes || task.description) && (
            <div>
              <Label className="text-xs font-medium text-[#8C8078] uppercase tracking-wider">Description</Label>
              <p className="mt-1 text-sm text-[#5D524D] bg-[#FDFCFB] border border-[#E6E0D9] rounded-lg px-3 py-2" data-testid="task-detail-description">
                {task.notes || task.description}
              </p>
            </div>
          )}

          {/* Dependencies */}
          {deps.length > 0 && (
            <div>
              <Label className="text-xs font-medium text-[#8C8078] uppercase tracking-wider">Dependencies</Label>
              <div className="mt-1 space-y-1.5">
                {deps.map((dep, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200" data-testid={`task-dep-${i}`}>
                    <Lock className="w-3.5 h-3.5" />
                    <span>Depends on task: {dep.taskId || dep}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Subtasks */}
          {subtasks.length > 0 && (
            <div>
              <Label className="text-xs font-medium text-[#8C8078] uppercase tracking-wider">
                Subtasks ({completedSubs}/{subtasks.length})
              </Label>
              <div className="mt-2 space-y-1">
                {subtasks.map((st, i) => {
                  const title = typeof st === 'string' ? st : (st.title || st.item || '');
                  const done = st.completed || false;
                  return (
                    <button
                      key={i}
                      onClick={() => handleSubtaskToggle(i)}
                      className={cn(
                        "w-full flex items-center gap-2.5 text-sm rounded-lg px-3 py-2 text-left transition-colors border",
                        done ? "bg-green-50 border-green-200 text-green-700" : "bg-white border-[#E6E0D9] text-[#2C2420] hover:bg-[#F5F3F0]"
                      )}
                      data-testid={`subtask-toggle-${i}`}
                    >
                      {done ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" /> : <Circle className="w-4 h-4 text-[#D4CCC6] shrink-0" />}
                      <span className={cn(done && "line-through")}>{title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Context Link */}
          <a
            href={contextLink}
            className="flex items-center gap-2 text-sm text-[#4A3B32] hover:underline font-medium"
            data-testid="task-detail-context-link"
          >
            <ExternalLink className="w-4 h-4" />
            View in {isClient ? 'Client Workflow' : 'Lead Tasks'}
            <ChevronRight className="w-4 h-4 ml-auto" />
          </a>

          {/* Comments */}
          <div>
            <Label className="text-xs font-medium text-[#8C8078] uppercase tracking-wider">
              <MessageSquare className="w-3.5 h-3.5 inline mr-1" />
              Comments ({comments.length})
            </Label>
            <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
              {loadingComments ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-[#8C8078]" />
                </div>
              ) : comments.length === 0 ? (
                <p className="text-xs text-[#8C8078] text-center py-3">No comments yet</p>
              ) : (
                comments.map(c => (
                  <div key={c.id} className="bg-[#FDFCFB] border border-[#E6E0D9] rounded-lg px-3 py-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-[#2C2420]">{c.author || c.createdBy}</span>
                      <span className="text-[10px] text-[#8C8078]">
                        {c.createdAt ? format(parseISO(c.createdAt), 'MMM d, h:mm a') : ''}
                      </span>
                    </div>
                    <p className="text-xs text-[#5D524D]">{c.content}</p>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="text-sm h-9"
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAddComment()}
                data-testid="task-detail-comment-input"
              />
              <Button
                size="sm"
                onClick={handleAddComment}
                disabled={!newComment.trim() || submitting}
                className="h-9 bg-[#4A3B32] hover:bg-[#3A2E26]"
                data-testid="task-detail-comment-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
